import apiClient from '@strava/api/lib/axios-client';
import { StatusCodes } from '@strava/constants/src/httpStatusCodes';
import { FOLLOWING_STATUS } from '@strava/constants/src/followingConstants';
import { convertKeysToCamel } from '@strava/utils';

/**
 * @typedef {Object} CurrentAthlete
 *
 * @property {number} id
 * @property {string} firstname
 * @property {string} lastname
 * @property {number} athleteType
 * @property {boolean} isSubscriber
 * @property {boolean} isSuperUser
 * @property {string} profileMedium
 * @property {string} profileLarge
 * @property {string} measurementUnits
 * @property {string} datePreference
 * @property {string} chartPreference
 * @property {string} viewPreference
 * @property {Object} features
 * @property {Object} experiments
 * @property {Object} preferences
 * @property {boolean} inPreview
 * @property {number} numDaysRemainingInPreview
 * */

/**
 * @description Fetches the current logged in athlete.
 *
 * @returns {Promise.<CurrentAthlete>} currentAthlete
 * */
export const getCurrentAthlete = async () => {
  const currentAthlete = await apiClient().get(`/frontend/athletes/current`);
  return convertKeysToCamel(currentAthlete.data.currentAthlete);
};

/**
 *  Follows an athlete on strava.
 *
 * @param {Object} param
 * @param {string} param.followerId - id for the athlete who is requesting the follow
 * @param {string} param.followingId - id for the athlete to be followed
 * @returns {Promise<FOLLOWING_STATUS>} - enum value PENDING or FOLLOWING
 */
export const followAthlete = ({ followerId, followingId, logError }) => {
  return new Promise((resolve) => {
    apiClient()
      .post(`/athletes/${followerId}/follows`, {
        follow: {
          follower_id: followerId,
          following_id: followingId
        }
      })
      .then((response) => {
        if (response?.status === StatusCodes.OK) {
          const { data } = response;
          if (data.approved) {
            resolve(FOLLOWING_STATUS.following);
          } else {
            resolve(FOLLOWING_STATUS.pending);
          }
        } else {
          resolve(FOLLOWING_STATUS.error);
          logError(response.error);
        }
      })
      .catch((e) => {
        resolve(FOLLOWING_STATUS.error);
        logError(e);
      });
  });
};

export const bulkFollow = ({ followeeIds, logError }) => {
  return new Promise((resolve) => {
    apiClient()
      .post('/athlete/bulk_follow', {
        followee_ids: followeeIds
      })
      .then((response) => {
        const { success, followed_athlete_ids: ids } = response.data;
        resolve({ success, followedAthleteIds: ids || [] });
      })
      .catch((e) => {
        logError(e);
        resolve({ success: false });
      });
  });
};

export const unfollowAthlete = ({ followerId, followingId, logError }) => {
  return new Promise((resolve) => {
    apiClient()
      .delete(
        `/athletes/${followerId}/follows/${followingId}`,
        {
          data: {
            follow: {
              follower_id: followerId,
              following_id: followingId
            }
          }
        },
        { headers: { Accept: 'text/javascript' } }
      )
      .then((response) => {
        if (response?.status === StatusCodes.NO_CONTENT) {
          resolve(true);
        } else {
          logError(response);
          resolve(false);
        }
      })
      .catch((e) => {
        logError(e);
        resolve(false);
      });
  });
};

/**
 * Gets suggested follow list
 *
 * @param {Object} param
 * @param {string} param.callsite - source making the get request
 * @returns {Promise<Array>} - list of suggested friends to follow
 */
export const getSuggestedFollows = ({ callsite, logError }) => {
  return new Promise((resolve, reject) => {
    apiClient()
      .get('/athlete/suggested_follows', {
        params: { callsite }
      })
      .then((response) => {
        if (response?.status === StatusCodes.OK) {
          const { data } = response;
          resolve(data);
        } else {
          logError(response.error);
          resolve(response.error);
        }
      })
      .catch((e) => {
        logError(e);
        reject(e);
      });
  });
};

export const postEmailContactsOnStrava = ({ importedEmails, logError }) => {
  return new Promise((resolve, reject) => {
    apiClient()
      .post('/athletes/email_contacts_on_strava', {
        emails: importedEmails
      })
      .then((response) => {
        if (response?.status === StatusCodes.OK) {
          resolve(response);
        } else {
          logError(response.error);
          resolve(response);
        }
      })
      .catch((e) => {
        logError(e);
        reject(e);
      });
  });
};

export const deleteUnderageAthlete = ({ logError, locale }) => {
  return new Promise((resolve, reject) => {
    apiClient({ currentLanguage: locale })
      .delete('/athlete/destroy_u13')
      .then((response) => {
        if (response?.status === StatusCodes.OK) {
          resolve(response);
        } else {
          logError(response.error);
          resolve(response);
        }
      })
      .catch((e) => {
        logError(e);
        reject(e);
      });
  });
};

/**
 * @param {Object} params
 * @param {string} params.viewingAthleteId
 * @param {string} params.followingId
 * @param {string} params.followerId
 *
 * @return {Promise<unknown>}
 * */
export const postAthleteFollow = ({
  viewingAthleteId,
  followingId,
  followerId
}) => {
  return apiClient().post(`/athletes/${viewingAthleteId}/follows`, {
    follow: {
      following_id: followingId,
      follower_id: followerId
    }
  });
};

/**
 * @param {Object} params
 * @param {string} params.viewingAthleteId
 * @param {string} params.followId
 *
 * @return {Promise<unknown>}
 * */
export const deleteAthleteFollow = ({ viewingAthleteId, followId }) =>
  apiClient().delete(`/athletes/${viewingAthleteId}/follows/${followId}`);

/**
 * @param {Object} params
 * @param {string} [params.entity]
 * @param {string} [params.entityId]
 * @param {string} [params.surfaceType]
 *
 * @return {Promise<unknown>}
 * */
export const fetchMentionableEntities = async ({
  entity,
  entityId,
  surfaceType
}) => {
  let url = '/athlete/mentionable_entities';

  const params = new URLSearchParams();
  if (entity && entityId) {
    params.append('parent_type', entity);
    params.append('parent_id', entityId);
  }
  if (surfaceType) {
    params.append('surface_type', surfaceType);
  }

  const queryString = params.toString();
  if (queryString) {
    url += `?${queryString}`;
  }

  return apiClient().get(url);
};

export const putAthleteAttributes = ({ athleteId, attributes, logError }) => {
  return new Promise((resolve) => {
    apiClient({
      options: {
        headers: { accept: 'text/javascript, application/javascript' }
      }
    })
      .put(`/athletes/${athleteId}`, { athlete: attributes })
      .then((response) => {
        if (response?.error) {
          logError(response.error);
        }
        resolve(response);
      })
      .catch((e) => {
        logError(e);
        resolve(e);
      });
  });
};

export const updateAthleteSettings = ({ settings, logError }) => {
  return new Promise((resolve, reject) => {
    apiClient()
      .put('/athlete_settings', { settings })
      .then((response) => {
        if (response?.error) {
          logError(response.error);
        }
        resolve(response);
      })
      .catch((e) => {
        logError(e);
        reject(e);
      });
  });
};

export const getDataSharingOptOut = ({ logError }) => {
  return new Promise((resolve) => {
    apiClient()
      .get('/athlete_settings/data_sharing')
      .then((response) => {
        if (response?.error) {
          logError(response.error);
        }
        resolve(response);
      })
      .catch((e) => {
        logError(e);
        resolve(e);
      });
  });
};

export default {
  postEmailContactsOnStrava,
  followAthlete,
  unfollowAthlete,
  getSuggestedFollows,
  deleteUnderageAthlete,
  putAthleteAttributes,
  updateAthleteSettings,
  getDataSharingOptOut
};
